import { CheckCircle2 } from "lucide-react";
import { services } from "../data/services";
import { citiesByProvince } from "../data/cities";
import {
  FR_SLUGS,
  FR_SERVICE_SLUGS,
  frServiceNames,
  frServiceShorts,
  frServiceDeliverables,
  frServiceCityParas,
  frServiceCityFaqs,
  frLabels,
  frCityName,
  resolveFrSlug,
} from "../data/frContent";
import { getHead } from "../seo/routes";
import { Seo } from "../components/Seo";
import { PageHero, Prose, FAQBlock, CTABand, Breadcrumbs, LinkGrid, SectionHead } from "../components/Blocks";
import NotFound from "./NotFound";

export function FrCityPage({ frCity }) {
  const path = `/fr/${frCity.slug}`;
  const head = getHead(path);
  const quebecCities = citiesByProvince("quebec").filter((c) => c.slug !== frCity.citySlug);
  return (
    <>
      <Seo title={head.title} description={head.description} path={path} lang="fr-CA" alternates={head.alternates} />
      <Breadcrumbs items={[{ label: frLabels.breadcrumbHome, to: "/" }, { label: frLabels.breadcrumbAreas, to: "/service-areas" }, { label: "Québec", to: "/security-guards-quebec" }, { label: frCity.name }]} />
      <PageHero overline={frCity.overline} title={frCity.h1} lead={frCity.lead} />
      <Prose paragraphs={frCity.paras} />
      <LinkGrid
        overline="Services locaux"
        title={`${frLabels.localServices} à ${frCity.name}`}
        cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        items={services.map((s) => ({
          to: `/fr/${FR_SERVICE_SLUGS[s.slug]}-a-${frCity.citySlug}`,
          label: frServiceNames[s.slug],
          testid: `fr-city-service-${s.slug}`,
        }))}
      />
      <FAQBlock faqs={frCity.faqs} heading={`${frLabels.faqHeading} — ${frCity.name}`} />
      <LinkGrid
        overline="À proximité"
        title={frLabels.nearby}
        items={quebecCities.map((c) => ({ to: `/fr/${FR_SLUGS[c.slug]}`, label: frCityName(c.slug), testid: `fr-nearby-${c.slug}` }))}
      />
      <CTABand title={frLabels.ctaTitle(frCity.name)} sub={frLabels.ctaSub} />
    </>
  );
}

export function FrServiceCityPage({ service, city }) {
  const path = `/fr/${FR_SERVICE_SLUGS[service.slug]}-a-${city.slug}`;
  const head = getHead(path);
  const sName = frServiceNames[service.slug];
  const cName = frCityName(city.slug);
  const faqs = frServiceCityFaqs(service, city);
  const otherServices = services.filter((s) => s.slug !== service.slug);
  const nearbyCities = citiesByProvince("quebec").filter((c) => c.slug !== city.slug).slice(0, 12);
  return (
    <>
      <Seo title={head.title} description={head.description} path={path} lang="fr-CA" alternates={head.alternates} />
      <Breadcrumbs items={[{ label: frLabels.breadcrumbHome, to: "/" }, { label: cName, to: `/fr/${FR_SLUGS[city.slug]}` }, { label: sName }]} />
      <PageHero overline={`${sName} · ${cName}, QC`} title={`${sName} à ${cName}`} lead={frServiceShorts[service.slug]} image={service.image} />
      <Prose paragraphs={frServiceCityParas(service, city)} />
      <section className="bg-[#131620] border-y border-white/10" data-testid="fr-servicecity-deliverables">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHead overline={frLabels.scope} title={frLabels.included(sName, cName)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {frServiceDeliverables[service.slug].map((d) => (
              <div key={d} className="flex items-start gap-3 border border-white/10 bg-[#0D0E12] p-4 rounded-sm">
                <CheckCircle2 className="h-5 w-5 text-[#FF5500] shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300 leading-relaxed">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FAQBlock faqs={faqs} heading={`${frLabels.faqHeading} — ${sName} à ${cName}`} />
      <LinkGrid
        overline={frLabels.moreInCity}
        title={frLabels.otherServicesTitle(cName)}
        cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        items={otherServices.map((s) => ({
          to: `/fr/${FR_SERVICE_SLUGS[s.slug]}-a-${city.slug}`,
          label: frServiceNames[s.slug],
          testid: `fr-other-service-${s.slug}`,
        }))}
      />
      <LinkGrid
        overline="À proximité"
        title={frLabels.nearbyTitle(sName)}
        items={nearbyCities.map((c) => ({
          to: `/fr/${FR_SERVICE_SLUGS[service.slug]}-a-${c.slug}`,
          label: frCityName(c.slug),
          testid: `fr-nearby-service-${c.slug}`,
        }))}
      />
      <CTABand title={frLabels.ctaServiceTitle(sName, cName)} sub={frLabels.ctaServiceSub} />
    </>
  );
}

export function FrResolver({ slug }) {
  const resolved = resolveFrSlug(slug || "");
  if (!resolved) return <NotFound />;
  if (resolved.kind === "city") return <FrCityPage frCity={resolved.frCity} />;
  return <FrServiceCityPage service={resolved.service} city={resolved.city} />;
}
